import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Crown, KeyRound, Pencil, Trash2 } from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
import { Input } from './Input';
import { Modal } from './Modal';
import { PosRoleSelector } from './PosRoleSelector';
import { useTeam, usePosUserRoles } from '../api/hooks';
import { updateTeamMember, removeTeamMember, transferBusinessOwnership, type TeamMember } from '../api/resources';
import { adminResetUserPassword } from '../api/admin';
import { formatDate } from '../lib/format';

/**
 * A business's members with LIVE POS roles + row actions — shared by the admin
 * BusinessDetail Users tab and the reseller CustomerDetail Users tab.
 *
 * Data: GET /businesses/:id/team (RLS: member / owning reseller / platform) merged
 * with GET /businesses/:id/pos-user-roles (live truth from the POS DB — legacy
 * members have no stored posRole and roles change inside the POS anyway).
 * Actions: PATCH/DELETE /businesses/:id/team/:userId (platform + owning reseller).
 * `canResetPassword` gates the reset action (it rides the platform-only
 * /admin/users/:id/reset-password endpoint).
 */
export function BusinessUsersTable({ businessId, canResetPassword = false, onChanged }: {
  businessId: string;
  canResetPassword?: boolean;
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const { data: members = [], isLoading } = useTeam(businessId);
  const { data: posRoles = [] } = usePosUserRoles(businessId);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [resetting, setResetting] = useState<TeamMember | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [promoting, setPromoting] = useState<TeamMember | null>(null);

  function livePos(m: TeamMember): { role: string | null; allowLogin: boolean } | null {
    const hit = posRoles.find(
      (p) =>
        (m.username && p.username && p.username === m.username) ||
        (m.email && p.email && p.email.toLowerCase() === m.email.toLowerCase()),
    );
    return hit ? { role: hit.role, allowLogin: hit.allowLogin } : null;
  }

  function changed() {
    void qc.invalidateQueries({ queryKey: ['team', businessId] });
    void qc.invalidateQueries({ queryKey: ['posUserRoles', businessId] });
    onChanged?.();
  }

  if (isLoading) {
    return (
      <div className="px-6 py-6 space-y-2" aria-busy="true">
        <div className="skeleton h-10 rounded" />
        <div className="skeleton h-10 rounded" />
        <div className="skeleton h-10 rounded" />
      </div>
    );
  }
  if (members.length === 0) {
    return <div className="px-6 py-8 text-sm text-zinc-500 dark:text-zinc-400">No users linked to this business yet.</div>;
  }

  return (
    <div className="overflow-x-auto nice-scroll">
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
          <tr>
            <th className="text-left font-medium px-6 py-3">User</th>
            <th className="text-left font-medium px-4 py-3">Portal role</th>
            <th className="text-left font-medium px-4 py-3">POS role</th>
            <th className="text-left font-medium px-4 py-3">Joined</th>
            <th className="text-right font-medium px-6 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
          {members.map((m) => {
            const pos = livePos(m);
            const posLabel = pos?.role ?? m.posRole ?? null;
            return (
              <tr key={m.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                <td className="px-6 py-3">
                  <div className="font-medium text-zinc-950 dark:text-zinc-50">
                    {m.name}
                    {m.status !== 'active' && (
                      <span className="ml-2 inline-block align-middle">
                        <Badge tone={m.status === 'suspended' ? 'error' : m.status === 'invited' ? 'info' : 'neutral'}>
                          {/* A pending invite holds a plan user seat but has no account yet —
                              say so plainly rather than showing a bare "invited". */}
                          {m.pendingInvite ? 'invite pending' : m.status}
                        </Badge>
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                    {m.username ? <span className="font-mono text-zinc-600 dark:text-zinc-300">@{m.username}</span> : null}
                    {m.username && m.email && m.email !== m.username ? <span className="mx-1 text-zinc-300 dark:text-zinc-600">·</span> : null}
                    {m.email && m.email !== m.username ? m.email : null}
                    {!m.username && !m.email ? '—' : null}
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 mono text-xs">{m.role}</td>
                <td className="px-4 py-3 text-xs">
                  {posLabel ? (
                    <span className="text-zinc-700 dark:text-zinc-200">
                      {posLabel}
                      {pos && !pos.allowLogin && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-wide text-rose-600 dark:text-rose-400">login off</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-zinc-400 dark:text-zinc-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{m.joinedAt ? formatDate(m.joinedAt) : '—'}</td>
                <td className="px-6 py-3">
                  {/* An invite has no user account behind it: editing, resetting a password
                      or "removing a member" would all 404. Offer nothing but the truth until
                      it is accepted. */}
                  {m.pendingInvite ? (
                    <div className="flex items-center justify-end">
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">Awaiting acceptance</span>
                    </div>
                  ) : (
                  <div className="flex items-center justify-end gap-1">
                    <button
                      type="button"
                      title="Edit user"
                      onClick={() => setEditing(m)}
                      className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {/* A greyed icon whose only explanation is a `title` tooltip is silent on
                        touch. Render the REASON instead of a dead control — it is also
                        actionable, since the fix is to add an email to the user. */}
                    {canResetPassword && (m.email ? (
                      <button
                        type="button"
                        title="Send password reset"
                        onClick={() => setResetting(m)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="px-1.5 text-[11px] text-zinc-400 dark:text-zinc-500 self-center whitespace-nowrap">
                        no email
                      </span>
                    ))}
                    {/* #3 — transfer ownership. Only offered on members who are NOT
                        already the owner: the new owner must already be a member, so
                        this row IS the picker and there is no email field to mistype. */}
                    {m.role !== 'owner' && (
                      <button
                        type="button"
                        title="Make this user the business owner"
                        onClick={() => setPromoting(m)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 transition"
                      >
                        <Crown className="w-4 h-4" />
                      </button>
                    )}
                    {m.role === 'owner' ? (
                      <span className="px-1.5 text-[11px] text-zinc-400 dark:text-zinc-500 self-center whitespace-nowrap">
                        owner — cannot remove
                      </span>
                    ) : (
                      <button
                        type="button"
                        title="Remove from business"
                        onClick={() => setRemoving(m)}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:text-rose-400 dark:hover:bg-rose-950/40 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <EditMemberModal
        member={editing}
        businessId={businessId}
        currentPosRole={editing ? (livePos(editing)?.role ?? editing.posRole ?? null) : null}
        onClose={() => setEditing(null)}
        onChanged={changed}
      />
      {canResetPassword && <ResetPasswordModal member={resetting} onClose={() => setResetting(null)} />}
      <RemoveMemberModal member={removing} businessId={businessId} onClose={() => setRemoving(null)} onChanged={changed} />
      <TransferOwnershipModal member={promoting} businessId={businessId} onClose={() => setPromoting(null)} onChanged={changed} />
    </div>
  );
}

/** Edit a member: SaaS display name, portal role, and POS role (pushed live into the POS DB). */
function EditMemberModal({ member, businessId, currentPosRole, onClose, onChanged }: {
  member: TeamMember | null;
  businessId: string;
  currentPosRole: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('staff');
  const [posRole, setPosRole] = useState('');
  const [initialPosRole, setInitialPosRole] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState<string | null>(null);
  if (member && seeded !== member.id) {
    setName(member.name);
    setRole(member.role === 'owner' ? 'owner' : member.role);
    setPosRole(currentPosRole ?? '');
    setInitialPosRole(currentPosRole);
    setError(null);
    setSeeded(member.id);
  }
  async function save() {
    if (!member) return;
    if (!name.trim()) { setError('Name cannot be empty.'); return; }
    setBusy(true); setError(null);
    try {
      await updateTeamMember(businessId, member.id, {
        ...(name.trim() !== member.name ? { name: name.trim() } : {}),
        ...(member.role !== 'owner' && role !== member.role ? { role: role as 'admin' | 'staff' | 'readonly' } : {}),
        ...(posRole && posRole !== (initialPosRole ?? '') ? { posRole } : {}),
      });
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  }
  const nothingToSave = !!member && name.trim() === member.name && (member.role === 'owner' || role === member.role) && (!posRole || posRole === (initialPosRole ?? ''));
  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Edit user"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={nothingToSave}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        {member?.role === 'owner' ? (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">This user is the business owner — the portal role is locked.</p>
        ) : (
          <div>
            <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Portal role</span>
            <div className="flex gap-2">
              {(['admin', 'staff', 'readonly'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition capitalize ${
                    role === r
                      ? 'border-brand bg-brand-subtle text-brand dark:bg-brand/15 dark:text-brand-dm dark:border-brand-dm'
                      : 'border-zinc-300 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
        <PosRoleSelector businessId={businessId} value={posRole} onChange={setPosRole} />
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          {initialPosRole
            ? <>Current POS role: <span className="font-medium">{initialPosRole}</span>. Picking another re-assigns it inside the POS immediately.</>
            : 'Picking a POS role re-assigns it inside the POS immediately.'}
        </p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** Email the member a password-reset (recovery) code — same flow as forgot-password. Platform only. */
function ResetPasswordModal({ member, onClose }: { member: TeamMember | null; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  function close() { setSentTo(null); setError(null); onClose(); }
  async function send() {
    if (!member) return;
    setBusy(true); setError(null);
    try {
      const res = await adminResetUserPassword(member.id);
      setSentTo(res.email);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the reset email.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={!!member}
      onClose={close}
      title="Reset password"
      size="sm"
      footer={sentTo ? (<Button onClick={close}>Done</Button>) : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={send} loading={busy}>Send reset email</Button>
        </>
      )}
    >
      {sentTo ? (
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          A password-reset code was emailed to <span className="font-medium">{sentTo}</span>.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Email <span className="font-medium text-zinc-900 dark:text-zinc-100">{member?.email}</span> a secure
            password-reset code (the same flow as “Forgot password”).
          </p>
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/** Remove a member — their POS login is disabled too (reversible inside the POS). */
function RemoveMemberModal({ member, businessId, onClose, onChanged }: {
  member: TeamMember | null;
  businessId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The API disables the POS login BEST-EFFORT: if the POS is unreachable it still removes
  // the SaaS membership and reports posLoginDisabled:false. The modal used to state
  // "POS login is disabled" as fact and close on success either way — so a removed user
  // could keep ringing up sales while the owner believed they had been cut off. Hold the
  // modal open and say exactly what did NOT happen.
  const [posWarning, setPosWarning] = useState(false);
  async function remove() {
    if (!member) return;
    setBusy(true); setError(null); setPosWarning(false);
    try {
      const res = await removeTeamMember(businessId, member.id);
      onChanged();
      if (res.posLoginDisabled === false) { setPosWarning(true); return; }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the user.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open={!!member}
      onClose={onClose}
      title="Remove user"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={remove} loading={busy}>Remove user</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Remove <span className="font-medium text-zinc-900 dark:text-zinc-100">{member?.name}</span>{' '}
          ({member?.email || member?.username}) from this business? They lose portal access and we{' '}
          <span className="font-medium text-zinc-900 dark:text-zinc-100">also try to disable their POS login</span>{' '}
          (re-enable inside the POS if needed). If this is their only business, the account is deactivated
          (soft delete) — nothing is permanently erased.
        </p>
        {posWarning && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
            <span className="font-medium">Removed from the portal, but their POS login is still active.</span>{' '}
            We couldn’t reach the POS to disable it. Open the POS for this business and deactivate
            <span className="font-medium"> {member?.username || member?.email}</span> there, or they can
            still sign in and ring up sales.
          </div>
        )}
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/**
 * Transfer ownership to another member (#3).
 *
 * The copy states BOTH halves of the locked decision, because "make owner" reads
 * like a pure promotion and it is not: the current owner is demoted at the same
 * moment. An admin who does not know that will be surprised by exactly one person
 * — the one who just lost their owner powers.
 */
function TransferOwnershipModal({
  member,
  businessId,
  onClose,
  onChanged,
}: {
  member: TeamMember | null;
  businessId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (!member) return;
    setBusy(true);
    setError(null);
    try {
      await transferBusinessOwnership(businessId, member.id);
      onChanged();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not transfer ownership. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={!!member}
      onClose={() => { if (!busy) { setError(null); onClose(); } }}
      title="Transfer ownership"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void confirm()} loading={busy} disabled={busy}>Make owner</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          <strong className="text-zinc-900 dark:text-zinc-100">{member?.name || member?.email}</strong> becomes
          the owner of this business — billing contact, owner-only settings and the owner portal.
        </p>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          The current owner stays on the team as a normal <strong>admin</strong>. They keep their login and
          their POS access, and lose only the owner-only powers.
        </p>
        <p className="text-xs text-zinc-500 dark:text-zinc-400">
          POS roles are unaffected — both users keep the POS role they have today.
        </p>
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}
